/**
 * Explains why the last sign-in did not finish, on the sign-in page. The API decided the outcome
 * and supplied the message; this component only picks the layout for the code it returned.
 *
 * @module apps/web/src/components/auth/SignInNotice
 */

import { useState } from 'react';
import { Ticket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FLUXACCOUNTS_JOIN_URL } from '@/lib/referralsApi';
import { readSignInFailure } from '@/lib/signInNotice';

/** Server code for a brand-new person refused while Fluxability is invite-only. */
const INVITE_ONLY = 'INVITE_ONLY';

/**
 * Shows the invite-only explanation with a link to FluxAccounts `/join`, or the server's message
 * for any other sign-in failure. Renders nothing when the last sign-in did not fail.
 */
export function SignInNotice() {
  const [failure] = useState(readSignInFailure);
  if (!failure) return null;

  if (failure.code === INVITE_ONLY) {
    return (
      <div
        role="alert"
        className="border-primary/30 bg-primary/5 space-y-3 rounded-md border p-3 text-sm"
      >
        <p className="font-medium">Fluxability is invite-only — ask a member for a code</p>
        <p className="text-muted-foreground">
          New accounts need a beta code or a referral code from someone who already uses
          Fluxability. Create your account on FluxAccounts with that code, then come back here and
          sign in. If you already have a Fluxability account, sign in the way you did before.
        </p>
        <Button asChild variant="outline" size="sm" className="w-full">
          <a href={FLUXACCOUNTS_JOIN_URL} rel="noopener noreferrer">
            <Ticket className="mr-2 h-4 w-4" />I have a code — join on FluxAccounts
          </a>
        </Button>
      </div>
    );
  }

  return (
    <p
      role="alert"
      className="border-destructive/30 text-destructive rounded-md border p-3 text-sm"
    >
      {failure.message}
    </p>
  );
}
